import { QuoteStateCode, QuoteStatusCode, QuoteType } from '@/lib/types'

type TaxCodeLike = {
  msdyn_taxcodeid?: string | null
  msdyn_name?: string | null
  statecode?: number | null
}

type QuoteTaxLike = {
  quoteid?: string | null
  statecode?: number | null
  statuscode?: number | null
  dsy_type?: number | null
  dsy_taxcode?: TaxCodeLike | null
  dsy_Branch?: { dsy_defaulttaxcode?: TaxCodeLike | null } | null
}

type AccountTaxLike = {
  msdyn_salestaxcode?: TaxCodeLike | null
}

const manualClearStoragePrefix = 'cpq:quote-tax-manual-clear:'
const manualClearMemory = new Set<string>()

export function resolveInitialQuoteTaxCodeId({
  quote,
  account,
  previousApprovedQuote,
  quoteTaxCodeManuallyCleared = false,
}: {
  quote: QuoteTaxLike
  account: AccountTaxLike
  previousApprovedQuote?: QuoteTaxLike | null
  quoteTaxCodeManuallyCleared?: boolean
}): string | null {
  if (quote.dsy_type === QuoteType.ChangeOrder) {
    if (quote.dsy_taxcode?.msdyn_taxcodeid) return null
    return previousApprovedQuote?.dsy_taxcode?.msdyn_taxcodeid ?? null
  }

  if (quote.dsy_taxcode) {
    return null
  }

  if (quoteTaxCodeManuallyCleared) {
    return null
  }

  return defaultTaxCode(quote, account)?.msdyn_taxcodeid ?? null
}

/**
 * The quote branch's active default tax code (in-state sales taxed at the seller's location) when the
 * customer's tax code is missing or in the same state, else the customer's active ZIP-based one.
 * Mirrors the DaisyTransactionPricingPlugins quote Create rule.
 */
function defaultTaxCode(
  quote: QuoteTaxLike | null | undefined,
  account: AccountTaxLike | null | undefined
): TaxCodeLike | null {
  const branchTaxCode = quote?.dsy_Branch?.dsy_defaulttaxcode
  const accountTaxCode = account?.msdyn_salestaxcode
  const branchState = taxState(branchTaxCode?.msdyn_name)
  if (
    branchTaxCode?.statecode === 0 &&
    branchTaxCode.msdyn_taxcodeid &&
    (!accountTaxCode || (branchState !== null && branchState === taxState(accountTaxCode.msdyn_name)))
  ) {
    return branchTaxCode
  }
  return accountTaxCode?.statecode === 0 ? accountTaxCode : null
}

/** Two-letter state prefix of a tax code name (IL_COOK_CHICAGO_WIO0 → IL), else null. */
function taxState(name: string | null | undefined): string | null {
  return name && name.length > 2 && name[2] === '_' ? name.slice(0, 2).toUpperCase() : null
}

export function canWriteQuoteAggregates(quote: QuoteTaxLike | null | undefined): boolean {
  if (!quote || quote.statecode !== QuoteStateCode.Draft || quote.statuscode === QuoteStatusCode.Draft_Rebasing) return false
  if (quote.dsy_type === QuoteType.ChangeOrder) return !!quote.dsy_taxcode?.msdyn_taxcodeid
  return quote.dsy_taxcode?.statecode === 0
}

export function canActivateQuoteWithTaxCode(quote: QuoteTaxLike | null | undefined): boolean {
  if (!quote) return false
  if (quote.dsy_type === QuoteType.ChangeOrder) return !!quote.dsy_taxcode?.msdyn_taxcodeid
  return quote.dsy_taxcode?.statecode === 0
}

export function quoteActivationTaxCodeError(quote: QuoteTaxLike | null | undefined): string | null {
  if (canActivateQuoteWithTaxCode(quote)) return null
  if (quote?.dsy_type === QuoteType.ChangeOrder) {
    return 'Select a tax code before activating this change order.'
  }
  return 'Select an active tax code before activating this quote.'
}

export function canToggleTaxExempt(quote: QuoteTaxLike | null | undefined): boolean {
  return !!quote && quote.dsy_type !== QuoteType.ChangeOrder
}

export function taxExemptDisableError(account: AccountTaxLike | null | undefined): string | null {
  return account === undefined
    ? 'Account tax is still loading. Please try again in a moment.'
    : null
}

export function quoteAggregateReadinessKey(quote: QuoteTaxLike | null | undefined): string {
  return [
    quote?.quoteid ?? '',
    quote?.statecode ?? '',
    quote?.dsy_type ?? '',
    quote?.dsy_taxcode?.msdyn_taxcodeid ?? '',
    quote?.dsy_taxcode?.statecode ?? '',
    canWriteQuoteAggregates(quote) ? 'ready' : 'blocked',
  ].join(':')
}

export function manualTaxSelectionPayloads(taxCodeId: string): {
  quote: { 'dsy_taxcode@odata.bind': string }
  account: null
} {
  return {
    quote: { 'dsy_taxcode@odata.bind': `/msdyn_taxcodes(${taxCodeId})` },
    account: null,
  }
}

/**
 * PATCH body for the account's default tax (the CPQ "Account default" block). Turning
 * exemption on also sends -Not Taxable-; turning it off sends only the flag, and the
 * SetAccountTaxCode plugin restores the ZIP code.
 */
export function accountTaxDefaultsPayload(
  change: { exempt: boolean; notTaxableId: string } | { exemptNumber: string } | { taxCodeId: string }
): Record<string, unknown> {
  if ('exempt' in change) {
    return change.exempt
      ? { msdyn_taxexempt: true, 'msdyn_salestaxcode@odata.bind': `/msdyn_taxcodes(${change.notTaxableId})` }
      : { msdyn_taxexempt: false }
  }
  if ('exemptNumber' in change) return { msdyn_taxexemptnumber: change.exemptNumber.trim() || null }
  return { 'msdyn_salestaxcode@odata.bind': `/msdyn_taxcodes(${change.taxCodeId})` }
}

/**
 * The tax code a new quote for this account would start with (the account's, or the branch
 * default for in-state sales) when this draft (non-change-order) quote uses a different one,
 * so CPQ can offer "Use default". Null otherwise.
 */
export function accountDefaultTaxMismatch(
  quote: QuoteTaxLike | null | undefined,
  account: AccountTaxLike | null | undefined
): TaxCodeLike | null {
  if (!quote || quote.statecode !== QuoteStateCode.Draft || quote.dsy_type === QuoteType.ChangeOrder) return null
  const taxCode = defaultTaxCode(quote, account)
  if (!taxCode?.msdyn_taxcodeid) return null
  return quote.dsy_taxcode?.msdyn_taxcodeid === taxCode.msdyn_taxcodeid ? null : taxCode
}

export function resolveTaxExemptRestoreTaxCodeId(
  quote: QuoteTaxLike | null | undefined,
  account: AccountTaxLike | null | undefined,
  excludedTaxCodeName?: string
): string | null {
  const taxCode = defaultTaxCode(quote, account)
  if (excludedTaxCodeName && taxCode?.msdyn_name === excludedTaxCodeName) return null
  return taxCode?.msdyn_taxcodeid ?? null
}

export function markQuoteTaxCodeManuallyCleared(quoteId: string | null | undefined): void {
  if (!quoteId) return
  manualClearMemory.add(quoteId)
  getManualClearStorage()?.setItem(`${manualClearStoragePrefix}${quoteId}`, '1')
}

export function clearQuoteTaxCodeManualClear(quoteId: string | null | undefined): void {
  if (!quoteId) return
  manualClearMemory.delete(quoteId)
  getManualClearStorage()?.removeItem(`${manualClearStoragePrefix}${quoteId}`)
}

export function wasQuoteTaxCodeManuallyCleared(quoteId: string | null | undefined): boolean {
  if (!quoteId) return false
  return manualClearMemory.has(quoteId) || getManualClearStorage()?.getItem(`${manualClearStoragePrefix}${quoteId}`) === '1'
}

function getManualClearStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}
